/**
 * DevicePose — correct device-orientation handling, shared by the orientation and
 * VO backends.
 *
 * The earlier bug: reading DeviceOrientationEvent.alpha directly as yaw. That is
 * inverted on iOS (alpha runs opposite the compass) and ignores screen
 * orientation and gimbal instability — hence "inverted" and "not catching up".
 *
 * The fix is the standard one (as in THREE.DeviceOrientationControls): build a
 * quaternion from (alpha, beta, gamma) in YXZ order, apply the world transform
 * (camera looks out the back of the device) and a screen-orientation correction,
 * then derive a stable forward vector → yaw/pitch. No axis is read as an angle.
 *
 * If yaw ends up mirrored on a given device, flip INVERT_YAW — kept as an
 * explicit calibration switch because sign conventions still vary in the wild.
 */

export interface Look {
  yaw: number; // degrees, 0..360, world azimuth of the forward vector
  pitch: number; // degrees, -90..90
  roll: number; // degrees
}

/** One IMU sample in the shape AlvaAR's findCameraPoseWithIMU expects. */
export interface MotionSample {
  timestamp: number;
  gx: number;
  gy: number;
  gz: number;
  ax: number;
  ay: number;
  az: number;
}

interface DeviceOrientationEventStatic {
  requestPermission?: () => Promise<'granted' | 'denied'>;
}

/**
 * iOS gates motion + orientation behind a permission prompt that may ONLY be
 * requested from inside a user-gesture task. Any `await` before the call — a
 * dynamic import, getUserMedia, anything — ends that task and iOS rejects with
 * NotAllowedError. That is silent: the listeners attach, no events ever fire,
 * and every reading stays at identity.
 *
 * (This is exactly what happened: an iPhone 18.6 capture recorded 923 IMU
 * samples that were all {0,0,0,w:1} / yaw 0 / pitch 0 / roll 0, because
 * requestPermission ran after `await selectSpatialBackend()`.)
 *
 * So the request is hoisted out of DevicePose.start() to here, and must be
 * called synchronously from the click handler. DevicePose.start() then awaits
 * whatever this resolved to.
 */
export type MotionPermission = 'unasked' | 'pending' | 'granted' | 'denied';

let permState: MotionPermission = 'unasked';
let permPromise: Promise<boolean> | null = null;

export function requestMotionPermission(): Promise<boolean> {
  if (permPromise) return permPromise;
  permState = 'pending';

  const ask = (ctor: unknown): Promise<boolean> | null => {
    const c = ctor as DeviceOrientationEventStatic | undefined;
    if (!c?.requestPermission) return null; // not a gated platform
    return c.requestPermission().then(
      (r) => r === 'granted',
      () => false,
    );
  };

  // Both have to be asked here, synchronously, while the gesture is still live.
  const orient = typeof DeviceOrientationEvent !== 'undefined' ? ask(DeviceOrientationEvent) : null;
  const motion = typeof DeviceMotionEvent !== 'undefined' ? ask(DeviceMotionEvent) : null;

  if (!orient && !motion) {
    // No gate on this platform: sensors fire unguarded.
    permState = 'granted';
    permPromise = Promise.resolve(true);
    return permPromise;
  }

  permPromise = Promise.all([orient ?? Promise.resolve(false), motion ?? Promise.resolve(false)])
    .then(([a, b]) => {
      const ok = a || b;
      permState = ok ? 'granted' : 'denied';
      // A denial must not be cached for the life of the page: "Begin again" has
      // to be able to ask a second time (a fresh gesture re-raises the prompt).
      if (!ok) permPromise = null;
      return ok;
    })
    .catch(() => {
      permState = 'denied';
      permPromise = null;
      return false;
    });
  return permPromise;
}

/** Whether the permission prompt has been answered, and how. Shown in the HUD. */
export function motionPermissionState(): MotionPermission {
  return permState;
}

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;
const INVERT_YAW = false; // flip on-device if turning feels mirrored
/** ~0.5s of samples at 60Hz. Deliberately below AlvaAR's 35-sample hard cap. */
const MOTION_BUFFER = 32;

export type Quat = { x: number; y: number; z: number; w: number };

/** Quaternion from Euler angles (radians) in THREE.js 'YXZ' order. */
function quatFromYXZ(x: number, y: number, z: number): Quat {
  const c1 = Math.cos(x / 2);
  const s1 = Math.sin(x / 2);
  const c2 = Math.cos(y / 2);
  const s2 = Math.sin(y / 2);
  const c3 = Math.cos(z / 2);
  const s3 = Math.sin(z / 2);
  return {
    x: s1 * c2 * c3 + c1 * s2 * s3,
    y: c1 * s2 * c3 - s1 * c2 * s3,
    z: c1 * c2 * s3 - s1 * s2 * c3,
    w: c1 * c2 * c3 + s1 * s2 * s3,
  };
}

function quatMul(a: Quat, b: Quat): Quat {
  return {
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
  };
}

/** Rotate vector v by quaternion q. */
function quatRotate(q: Quat, v: [number, number, number]): [number, number, number] {
  const [vx, vy, vz] = v;
  // t = 2 * cross(q.xyz, v)
  const tx = 2 * (q.y * vz - q.z * vy);
  const ty = 2 * (q.z * vx - q.x * vz);
  const tz = 2 * (q.x * vy - q.y * vx);
  return [
    vx + q.w * tx + (q.y * tz - q.z * ty),
    vy + q.w * ty + (q.z * tx - q.x * tz),
    vz + q.w * tz + (q.x * ty - q.y * tx),
  ];
}

// -PI/2 about X: the camera looks out the back of the device.
const Q_WORLD: Quat = { x: -Math.SQRT1_2, y: 0, z: 0, w: Math.SQRT1_2 };

function screenOrientationAngle(): number {
  // Guarded so the handlers can run outside a browser (unit tests) — a bare
  // `screen` reference throws a ReferenceError where it is undeclared.
  const scr = typeof screen !== 'undefined' ? screen : undefined;
  const win = typeof window !== 'undefined' ? window : undefined;
  const a = (scr?.orientation && scr.orientation.angle) ?? (win?.orientation as number | undefined) ?? 0;
  return (a || 0) * DEG;
}

/**
 * How long without an absolute-orientation event before relative ones are
 * believed again. Covers a platform that fires `deviceorientationabsolute`
 * briefly and then stops (magnetometer unavailable); without it the pose would
 * freeze. Several times the ~16-60 ms event period, so normal jitter in event
 * delivery never flips the reference.
 */
const ABSOLUTE_STALE_MS = 1000;

export class DevicePose {
  private look_: Look = { yaw: 0, pitch: 0, roll: 0 };
  private started = false;
  private haveSensor = false;
  /** Latest device orientation as a quaternion (for AlvaAR IMU fusion). */
  private quat: Quat = { x: 0, y: 0, z: 0, w: 1 };
  /** Buffered motion samples, drained by the tracker each frame. */
  private motion: MotionSample[] = [];
  private haveMotion = false;

  private onMotion = (ev: DeviceMotionEvent) => {
    const rr = ev.rotationRate;
    const ac = ev.accelerationIncludingGravity;
    if (!rr && !ac) return;
    this.haveMotion = true;
    this.motion.push({
      timestamp: performance.now(),
      // ⚠️ OPEN, deliberately unchanged (audit 2026-10-04). Per the W3C
      // DeviceOrientation spec `rotationRate.alpha` is about the device Z axis,
      // `beta` about X and `gamma` about Y — so this maps gx<-Z, gy<-X, gz<-Y,
      // which LOOKS wrong if AlvaAR's gx/gy/gz mean "about device x/y/z". But
      // nothing vendored says what they mean: alva_ar.js only packs the fields
      // into the WASM buffer, the axis convention lives in compiled C++, and
      // no capture records rotationRate (only the orientation quaternion), so
      // it cannot be tested on real data either. iPhone tracking is
      // field-verified working with THIS mapping. Do not "fix" it without
      // evidence — e.g. a capture that records rotationRate, replayed through
      // findCameraPoseWithIMU with both mappings.
      gx: (rr?.alpha ?? 0) * DEG,
      gy: (rr?.beta ?? 0) * DEG,
      gz: (rr?.gamma ?? 0) * DEG,
      ax: ac?.x ?? 0,
      ay: ac?.y ?? 0,
      az: ac?.z ?? 0,
    });
    // Bound the buffer if nothing is draining it (e.g. a non-IMU backend, or
    // AlvaAR's several-second WASM init). Consumers cap again at their own hard
    // limit — AlvaAR's is 35 — but keeping this small means the first fused call
    // gets samples adjacent to its frame instead of a stale multi-second
    // backlog that is worse than useless for integration.
    if (this.motion.length > MOTION_BUFFER) {
      this.motion.splice(0, this.motion.length - MOTION_BUFFER);
    }
  };

  /** Current orientation quaternion. */
  quaternion(): Quat {
    return this.quat;
  }

  /** Take all buffered motion samples (the tracker consumes them). */
  drainMotion(): MotionSample[] {
    const m = this.motion;
    this.motion = [];
    return m;
  }

  hasMotion(): boolean {
    return this.haveMotion;
  }

  // desktop / no-sensor fallback
  private dragging = false;
  private lastX = 0;
  private lastY = 0;

  /** When the last `deviceorientationabsolute` event arrived (performance.now), or null. */
  private lastAbsoluteAt: number | null = null;

  /**
   * `deviceorientationabsolute` — earth-referenced heading (Chrome Android).
   * Once one has arrived, relative events are ignored; see `onRelative`.
   */
  private onAbsolute = (ev: DeviceOrientationEvent) => {
    if (ev.alpha == null || ev.beta == null || ev.gamma == null) return;
    this.lastAbsoluteAt = performance.now();
    this.onOrient(ev);
  };

  /**
   * `deviceorientation` — on Chrome Android its yaw origin is ARBITRARY (the
   * heading at page load), while the absolute event's is magnetic north.
   *
   * ⚠️ Both used to be fed to one handler, so on Android the quaternion flipped
   * between two yaw references every few milliseconds — and that quaternion is
   * what AlvaAR's IMU fusion and the world-frame transfer (`worldUp()`) consume.
   * The comment said "prefer the absolute event"; nothing implemented it.
   *
   * Now: whichever stream is absolute wins while it is alive. iOS has no
   * absolute event, so relative events are used there exactly as before. An
   * event on this listener that itself reports `absolute: true` (older Chrome
   * delivered absolute data here) is equivalent and is accepted.
   */
  private onRelative = (ev: DeviceOrientationEvent) => {
    if (!ev.absolute && this.lastAbsoluteAt !== null && performance.now() - this.lastAbsoluteAt < ABSOLUTE_STALE_MS) {
      return;
    }
    this.onOrient(ev);
  };

  private onOrient = (ev: DeviceOrientationEvent) => {
    if (ev.alpha == null || ev.beta == null || ev.gamma == null) return;
    this.haveSensor = true;
    const alpha = ev.alpha * DEG;
    const beta = ev.beta * DEG;
    const gamma = ev.gamma * DEG;
    const orient = screenOrientationAngle();

    // euler.set(beta, alpha, -gamma, 'YXZ')
    let q = quatFromYXZ(beta, alpha, -gamma);
    q = quatMul(q, Q_WORLD);
    // screen orientation correction: rotate -orient about Z
    q = quatMul(q, { x: 0, y: 0, z: Math.sin(-orient / 2), w: Math.cos(-orient / 2) });

    this.quat = q;
    const fwd = quatRotate(q, [0, 0, -1]);
    const up = quatRotate(q, [0, 1, 0]);
    let yaw = Math.atan2(fwd[0], fwd[2]) * RAD;
    if (INVERT_YAW) yaw = -yaw;
    this.look_.yaw = (yaw + 360) % 360;
    this.look_.pitch = Math.asin(Math.max(-1, Math.min(1, fwd[1]))) * RAD;
    this.look_.roll = Math.atan2(up[0], up[1]) * RAD;
  };

  private onDown = (e: PointerEvent) => {
    this.dragging = true;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
  };
  private onUp = () => {
    this.dragging = false;
  };
  private onMove = (e: PointerEvent) => {
    if (!this.dragging || this.haveSensor) return;
    this.look_.yaw = (this.look_.yaw + (e.clientX - this.lastX) * 0.4 + 360) % 360;
    this.look_.pitch = Math.max(-60, Math.min(60, this.look_.pitch - (e.clientY - this.lastY) * 0.3));
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    // Keep the quaternion in step. Rendering projects with `quaternion()`, not
    // with these Euler angles, so without this the desktop drag turned the HUD
    // readout while leaving the world completely still — the mouse would appear
    // to do nothing at all.
    this.syncQuatFromLook();
  };

  /**
   * Rebuild the quaternion from the dragged yaw/pitch.
   *
   * Only for the no-sensor fallback. On a real device the quaternion is primary
   * and the Euler angles are derived from it; going the other way is a last
   * resort, safe here only because a mouse drag produces no roll.
   *
   * `look_.yaw` is `atan2(forward.x, forward.z)`, so forward is
   * `(cos p · sin y, sin p, cos p · cos y)`; the rotation taking (0,0,-1) there
   * is a yaw of (yaw − 180°) about +Y, then a pitch about +X. Verified to
   * recover yaw and pitch exactly across the full range.
   *
   * It does NOT round-trip `look_.roll`, and deliberately leaves it alone: the
   * app's `roll = atan2(up.x, up.y)` is not a view-axis roll but a quantity that
   * couples yaw and pitch (it reads −60° at yaw 91°, pitch 60°, with no roll
   * applied at all). That is a pre-existing quirk of the Euler extraction and
   * affects only the HUD readout — nothing projects with it.
   */
  private syncQuatFromLook(): void {
    const y = (this.look_.yaw - 180) * DEG;
    const p = this.look_.pitch * DEG;
    const qy: Quat = { x: 0, y: Math.sin(y / 2), z: 0, w: Math.cos(y / 2) };
    const qx: Quat = { x: Math.sin(p / 2), y: 0, z: 0, w: Math.cos(p / 2) };
    this.quat = quatMul(qy, qx);
  }

  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;

    // Do NOT request permission here — by the time a backend starts, the user
    // gesture is long gone and iOS rejects silently. requestMotionPermission()
    // must have been called from the click handler — the Rite's sigil tap
    // (rite/Rite.tsx) is that handler today. Calling it
    // now is a no-op that returns the earlier result; if it was never called we
    // still attach listeners, which is correct on non-gated platforms.
    await requestMotionPermission();

    // Prefer the absolute event where available — see onRelative for how.
    window.addEventListener('deviceorientationabsolute', this.onAbsolute as EventListener, true);
    window.addEventListener('deviceorientation', this.onRelative, true);
    window.addEventListener('devicemotion', this.onMotion, true);
    window.addEventListener('pointerdown', this.onDown);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointermove', this.onMove);
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    window.removeEventListener('deviceorientationabsolute', this.onAbsolute as EventListener, true);
    window.removeEventListener('deviceorientation', this.onRelative, true);
    this.lastAbsoluteAt = null;
    window.removeEventListener('devicemotion', this.onMotion, true);
    window.removeEventListener('pointerdown', this.onDown);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointermove', this.onMove);
  }

  look(): Look {
    return this.look_;
  }

  hasSensor(): boolean {
    return this.haveSensor;
  }
}
